import { Component, ViewEncapsulation, ChangeDetectionStrategy, ChangeDetectorRef, OnInit, OnDestroy, ElementRef, ViewChild, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';

import { Client } from 'src/providers/client/client';

import { SeamService, RemoteAccessAddress, RemoteAccessDeviceWithAssignments } from 'src/shared/services/seam.service';
import { WindowService } from 'src/shared/providers/window.service';
import { Util } from 'src/shared/util/util';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { DemoDataService } from 'src/shared/services/demo-data.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { SmartLockChooserComponent } from 'src/shared/components/smart-lock-chooser/smart-lock-chooser.component';
import { ManageLockAssignmentsComponent } from 'src/shared/components/manage-lock-assignments/manage-lock-assignments.component';
import { SmartlockTestCodeComponent } from 'src/shared/components/smartlock-test-code/smartlock-test-code.component';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

@Component({
  selector: 'access-content',
  templateUrl: './access-content.component.html',
  styleUrls: ['./access-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AccessContentComponent implements OnInit, OnDestroy {
  readonly windowService = inject(WindowService);
  private readonly seamService = inject(SeamService);
  private readonly client = inject(Client);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly util = inject(Util);
  private readonly aboutThisPageService = inject(AboutThisPageService);
  private readonly demoDataService = inject(DemoDataService);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  private readonly navCtrl = inject(CustomNavController);
  private readonly translationPipe = new TranslationPipe();
  private panelClosedSubscription: Subscription | null = null;

  loaded = false;
  hasIntegration = false;
  headers: string[] = [];
  columnLabels: string[] = [];
  shownRows: any[] = [];
  showAboutThisPage = false;
  isDemo = false;
  overlayTop: string;
  private _accessHeader: ElementRef;
  private overlayResizeObserver: ResizeObserver;
  @ViewChild('accessHeader', { read: ElementRef }) set accessHeader(el: ElementRef) {
    this._accessHeader = el;
    if (el && this.showAboutThisPage && !this.overlayResizeObserver) {
      this.setupOverlayObserver();
    }
  }

  async ngOnInit() {
    this.buildHeaders();
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('access');
    this.isDemo = this.demoDataService.isDemoAccount();
    if (this.showAboutThisPage || this.isDemo) {
      this.hasIntegration = true;
      this.buildHeaders();
      this.shownRows = this.buildMockRows();
      this.loaded = true;
      this.cdr.markForCheck();
      return;
    }
    await this.fetchAccessData();
  }

  private async fetchAccessData() {
    try {
      const integrations = await this.seamService.getExistingIntegration();
      this.hasIntegration = integrations.length > 0;
      if (this.hasIntegration) {
        const [devices, addresses, remoteAccessAddresses] = await Promise.all([
          this.seamService.getRemoteAccessDevices(),
          this.client.getAddresses(),
          this.seamService.getRemoteAccessAddresses()
        ]);
        const addressNameById = this.buildAddressNameMap(addresses);
        const addressColorById = this.buildAddressColorMap(addresses);
        const addressIdsByRaaId = this.buildAddressIdsByRaaId(remoteAccessAddresses);
        this.buildHeaders();
        this.shownRows = this.buildRows(devices, addressNameById, addressColorById, addressIdsByRaaId);
      }
    } catch (err) {
      this.util.showError('Error loading smart locks');
    }
    this.loaded = true;
    this.cdr.markForCheck();
  }

  private buildAddressNameMap(addresses: any[]): Record<number, string> {
    const map: Record<number, string> = {};
    for (const a of addresses ?? []) {
      map[a.id] = a.address_name || this.client.buildAddressName?.(a) || a.address1 || '';
    }
    return map;
  }

  private buildAddressColorMap(addresses: any[]): Record<number, string> {
    const map: Record<number, string> = {};
    for (const a of addresses ?? []) {
      map[a.id] = a.color || '#1E2125';
    }
    return map;
  }

  private buildHeaders() {
    this.headers = this.windowService.isDesktopRes
      ? ['Lock', 'Properties', 'Status', 'Test', 'last-column']
      : ['Lock', 'Properties', 'Test'];
    this.columnLabels = this.windowService.isDesktopRes
      ? ['Lock', 'Properties', 'Status', '', '']
      : ['Lock', 'Properties', ''];
  }

  // The devices endpoint does not return each lock's address assignments — the
  // many-to-many join lives on the remote-access-addresses endpoint, keyed by
  // each RAA's nested addresses[]. Resolve a device's assigned Tidy address ids
  // through its remote_access_address_id.
  private buildAddressIdsByRaaId(remoteAccessAddresses: RemoteAccessAddress[]): Map<number, number[]> {
    const map = new Map<number, number[]>();
    for (const raa of remoteAccessAddresses ?? []) {
      map.set(raa.id, (raa.addresses ?? []).map(address => address.id));
    }
    return map;
  }

  private buildRows(
    devices: RemoteAccessDeviceWithAssignments[],
    addressNameById: Record<number, string>,
    addressColorById: Record<number, string>,
    addressIdsByRaaId: Map<number, number[]>
  ): any[] {
    return devices.map(device => {
      const addressIds = addressIdsByRaaId.get(device.remote_access_address_id) ?? [];
      const names = addressIds
        .map(id => addressNameById[id])
        .filter(Boolean);
      const propertiesLabel = names.length > 0 ? names.join(', ') : this.translationPipe.transform('Unassigned');
      const firstAddressId = addressIds[0] ?? null;
      const tagColor = firstAddressId !== null
        ? addressColorById[firstAddressId]
        : null;
      const row: any = {
        remoteAccessDeviceId: device.id,
        firstAddressId,
        tagColor,
        statusLabel: device.in_use ? 'Active' : 'Inactive',
        'Lock': {
          value: device.display_name || device.device_name,
          icon: 'assets/svg/lock-closed-outline.svg',
        },
        'Properties': {
          value: propertiesLabel,
          color: tagColor || undefined,
          addressId: firstAddressId !== null ? firstAddressId.toString() : undefined,
        },
        'Test': {
          value: this.translationPipe.transform('Generate Access Code'),
          icon: 'assets/svg/key-outline.svg',
          action: () => this.openTestCode(device.id),
        },
      };
      if (this.windowService.isDesktopRes) {
        row['Status'] = {
          value: this.translationPipe.transform(device.in_use ? 'Active' : 'Inactive'),
        };
        row['last-column'] = {};
      }
      return row;
    });
  }

  private buildMockRows(): any[] {
    const mockLocks = [
      { name: 'Front Door - Yale Assure 2', properties: '123 Main St', status: 'Active' },
      { name: 'Back Gate - August Wi-Fi', properties: '123 Main St, 456 Oak Ave', status: 'Active' },
      { name: 'Side Entry - Schlage Encode', properties: '456 Oak Ave', status: 'Inactive' },
      { name: 'Main Entrance - Kwikset Halo', properties: '456 Oak Ave', status: 'Active' },
      { name: 'Pool Gate - Yale Assure Lock 2', properties: '789 Pine Rd', status: 'Active' },
      { name: 'Garage Door - August Smart Lock Pro', properties: '789 Pine Rd', status: 'Inactive' },
      { name: 'Front Door - Schlage Encode Plus', properties: '321 Elm Blvd', status: 'Active' },
      { name: 'Back Door - Kwikset Premis', properties: '321 Elm Blvd', status: 'Active' },
      { name: 'Guest Suite - Yale Conexis L2', properties: '654 Maple Dr', status: 'Inactive' },
      { name: 'Main Entry - August Wi-Fi Smart Lock', properties: '654 Maple Dr', status: 'Active' },
      { name: 'Front Door - Schlage Sense', properties: '987 Cedar Ln', status: 'Active' },
      { name: 'Storage Room - Yale Linus L2', properties: '987 Cedar Ln', status: 'Inactive' },
    ];
    return mockLocks.map(lock => {
      const row: any = {
        tagColor: '#4A90D9',
        firstAddressId: null,
        statusLabel: lock.status,
        'Lock': {
          value: lock.name,
          icon: 'assets/svg/lock-closed-outline.svg',
        },
        'Properties': {
          value: lock.properties,
          color: '#4A90D9',
        },
        'Test': {
          value: this.translationPipe.transform('Generate Access Code'),
          icon: 'assets/svg/key-outline.svg',
        },
      };
      if (this.windowService.isDesktopRes) {
        row['Status'] = {
          value: this.translationPipe.transform(lock.status),
        };
        row['last-column'] = {};
      }
      return row;
    });
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._accessHeader) {return;}
      const top = this._accessHeader.nativeElement.getBoundingClientRect().top - 8;
      this.overlayTop = `${top  }px`;
      this.cdr.markForCheck();
    };
    this.overlayResizeObserver = new ResizeObserver(() => updateTop());
    this.overlayResizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  ngOnDestroy() {
    this.overlayResizeObserver?.disconnect();
    this.panelClosedSubscription?.unsubscribe();
  }

  onCellClick(event: { column: string; element: any }) {
    if (event.column === 'Test' && event.element?.remoteAccessDeviceId) {
      this.openTestCode(event.element.remoteAccessDeviceId);
    }
  }

  async openTestCode(remoteAccessDeviceId: number) {
    if (this.windowService.isDesktopRes) {
      const desktopParams = { preselectedDeviceId: remoteAccessDeviceId };
      const isPanelOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isPanelOpen) {
        await this.rightSidePanelService.navigateTo('smartlock-test-code', desktopParams, SmartlockTestCodeComponent);
      } else {
        await this.storage.save('dialog-right-side-open', true);
        await this.storage.save('dialog-params', desktopParams);
        this.rightSidePanelService.openRightSidePanel(SmartlockTestCodeComponent);
      }
    } else {
      const mobileParams = { preselectedDeviceId: remoteAccessDeviceId };
      await this.storage.save('dialog-params', mobileParams);
      this.navCtrl.navigateForward('smartlock-test-code', mobileParams);
    }
  }

  async goToConnectLocks() {
    if (this.demoBlock.block()) { return; }
    // When no integration yet, skip the chooser entirely and land directly on
    // the wizard's Connect step (which auto-fires the Seam webview). Avoids
    // leaving an empty chooser route in the panel back stack.
    const targetRoute = this.hasIntegration ? 'smart-locks/chooser' : 'smart-locks/manage-assignments';
    const targetComponent = this.hasIntegration ? SmartLockChooserComponent : ManageLockAssignmentsComponent;
    const params: { startOnConnect?: boolean } = this.hasIntegration ? {} : { startOnConnect: true };

    if (this.windowService.isDesktopRes) {
      const isPanelOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isPanelOpen) {
        await this.rightSidePanelService.navigateTo(targetRoute, params, targetComponent);
      } else {
        await this.storage.save('dialog-right-side-open', true);
        await this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(targetComponent);
      }
      this.panelClosedSubscription?.unsubscribe();
      this.panelClosedSubscription = this.rightSidePanelService.panelClosed().subscribe(() => {
        this.reloadAccessData();
      });
    } else {
      await this.storage.save('dialog-params', params);
      this.navCtrl.navigateForward(targetRoute, params);
    }
  }

  private async reloadAccessData() {
    this.loaded = false;
    this.shownRows = [];
    this.cdr.markForCheck();
    await this.fetchAccessData();
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.overlayResizeObserver?.disconnect();
    this.aboutThisPageService.dismissAboutThisPage('access');
    this.shownRows = [];
    this.headers = [];
    this.hasIntegration = false;
    this.loaded = false;
    this.cdr.markForCheck();
    this.ngOnInit();
  }
}
